import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

/* ---------------- LIB & API ---------------- */
import { orderApi } from "./lib/orderApi";

/* ---------------- PAGES ---------------- */
import Layout from "./components/Layout";
import Home from "./pages/Home";
import About from "./pages/About";
import PublicMenu from "./pages/PublicMenu";
import Gallery from "./pages/Gallery";
import Social from "./pages/Social";
import Contact from "./pages/Contact";

/* ---------------- ORDERING PAGES ---------------- */
import SelectTable from "./pages/SelectTable";
import OrderMenu from "./pages/OrderMenu";
import Cart from "./pages/Cart";
import OrderConfirmation from "./pages/OrderConfirmation";

/* ---------------- DASHBOARDS ---------------- */
import Login from "./pages/Login";
import KitchenDashboard from "./pages/KitchenDashboard";
import CounterDashboard from "./pages/CounterDashboard";
import AdminDashboard from "./pages/AdminDashboard";

/* ---------------- AUTH CONTEXT ---------------- */
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

/* ---------------- CART CONTEXT ---------------- */
const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

/* ---------------- AUTH PROVIDER ---------------- */
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyAuth = async () => {
      try {
        const token = localStorage.getItem("token");
        const userData = localStorage.getItem("user");

        if (!token) {
          setUser(null);
          setLoading(false);
          return;
        }

        // Verify token with backend
        try {
          const verifiedUser = await orderApi.getMe();
          setUser(verifiedUser);
          localStorage.setItem("user", JSON.stringify(verifiedUser));
        } catch (err) {
          if (err.response && (err.response.status === 401 || err.response.status === 403)) {
            // Token is invalid, expired, or rejected
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            setUser(null);
          } else if (userData) {
            setUser(JSON.parse(userData));
          }
        }
      } catch {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, []);

  const login = async (email, password) => {
    const res = await orderApi.login(email, password);

    localStorage.setItem("token", res.access_token);
    localStorage.setItem("user", JSON.stringify(res.user));

    setUser(res.user);
    return res;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const getToken = () => localStorage.getItem("token");

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

/* ---------------- CART PROVIDER ---------------- */
function CartProvider({ children }) {
  const [cart, setCart] = useState([]);
  const [tableId, setTableId] = useState(null);
  const [tableName, setTableName] = useState(null);

  useEffect(() => {
    try {
      const savedCart = localStorage.getItem("cart");
      const savedTableId = localStorage.getItem("tableId");
      const savedTableName = localStorage.getItem("tableName");

      if (savedCart) setCart(JSON.parse(savedCart));
      if (savedTableId) setTableId(savedTableId);
      if (savedTableName) setTableName(savedTableName);
    } catch {
      localStorage.clear();
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((product) => {
    setCart((prev) => {
      const exist = prev.find((i) => String(i.id) === String(product.id));

      if (exist) {
        return prev.map((i) =>
          String(i.id) === String(product.id)
            ? { ...i, quantity: i.quantity + 1 }
            : i
        );
      }

      return [...prev, { ...product, quantity: 1 }];
    });
  }, []);

  const removeFromCart = useCallback((id) => {
    setCart((prev) => prev.filter((i) => String(i.id) !== String(id)));
  }, []);

  const updateQuantity = useCallback(
    (id, qty) => {
      if (qty <= 0) return removeFromCart(id);

      setCart((prev) =>
        prev.map((i) =>
          String(i.id) === String(id) ? { ...i, quantity: qty } : i
        )
      );
    },
    [removeFromCart]
  );

  const clearCart = useCallback(() => {
    setCart([]);
    localStorage.removeItem("cart");
  }, []);

  const setTable = useCallback((id, name) => {
    setTableId(id);
    setTableName(name);

    localStorage.setItem("tableId", String(id));
    if (name) localStorage.setItem("tableName", name);
  }, []);

  const cartTotal = cart.reduce(
    (sum, i) => sum + Number(i.price) * i.quantity,
    0
  );

  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        tableId,
        tableName,
        setTable,
        cartTotal,
        cartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

/* ---------------- PROTECTED ROUTE ---------------- */
function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading } = useAuth();
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 text-stone-700">
        <div className="w-9 h-9 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-xs font-bold uppercase tracking-wider text-stone-500">
          Verifying Staff Session...
        </p>
      </div>
    );
  }

  // Strict check: User must have valid token & authenticated session
  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles.length && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

/* ---------------- APP ---------------- */
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <Routes>
            {/* 🌟 1. PRIMARY EXPERIENCE: PORTFOLIO (WITH NAVBAR & FOOTER) */}
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/menu" element={<PublicMenu />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/social" element={<Social />} />
              <Route path="/contact" element={<Contact />} />
            </Route>

            {/* 🍨 2. SECONDARY EXPERIENCE: TABLE ORDERING */}
            <Route path="/select-table" element={<SelectTable />} />
            <Route path="/order" element={<OrderMenu />} />
            <Route path="/menu/order" element={<OrderMenu />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/order/:orderId" element={<OrderConfirmation />} />

            {/* 🔐 3. STAFF & ADMIN */}
            <Route path="/login" element={<Login />} />
            <Route
              path="/kitchen"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager", "staff"]}>
                  <KitchenDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/counter"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager", "staff"]}>
                  <CounterDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute allowedRoles={["admin", "manager"]}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* 4. CATCH-ALL */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          <Toaster position="top-center" richColors />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
